import { gql, type TypedDocumentNode } from "@apollo/client";
import type { CommissionProgramConsentNode } from "@/pages/billing/finance/commission/consents/types";
import type { CommissionEnrollmentAccountStatus } from "@/pages/billing/finance/commission/enrollments/types";
import type { PersonGrpContributionLineNode } from "./_utils/contributions";
import type { PersonGrpContributionTotalsLine } from "./_utils/contributionTotals";
import type { PersonGrpAllocationAgreementNode, PersonGrpPaidOutTotal } from "./types";
import type { GrpEnrollmentStatus } from "@/pages/billing/finance/grp/enrollments/types";
import type { PersonGrpEnrollmentNode } from "./_components/matchPersonGrpEnrollment";
import { CRM_NOTE_FIELDS_FRAGMENT } from "@/fragments";

export interface PersonGrpContextQueryResponse {
  person: {
    id: string;
    displayName: string;
    pgaId: string;
  };
}

export const PERSON_GRP_CONTEXT_QUERY: TypedDocumentNode<PersonGrpContextQueryResponse, { pgaId: string }> = gql`
  query PersonGrpContext($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      displayName
      pgaId
    }
  }
`;

export interface PersonGrpBenefitsQueryResponse {
  person: {
    id: string;
    displayName: string;
    pgaId: string;
    commissionBenefitProgram: {
      program: {
        id: string;
        name: string;
        slug: string;
      };
      enrollment: {
        id: string;
        accountStatus: CommissionEnrollmentAccountStatus;
        program: {
          id: string;
          name: string;
          slug: string;
        };
      } | null;
      programConsents: PersonGrpListConnection<CommissionProgramConsentNode>;
      allocationAgreements: PersonGrpListConnection<PersonGrpAllocationAgreementNode>;
      receivedAllocationAgreements: PersonGrpListConnection<PersonGrpAllocationAgreementNode>;
      lines: {
        nodes: PersonGrpContributionLineNode[];
        totalCount: number;
        pageInfo: {
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          startCursor?: string;
          endCursor?: string;
        };
      };
      paidOutTotal: PersonGrpPaidOutTotal;
    };
  };
}

/** One page of a person GRP list; `pageInfo` drives `fetchRemainingPages` to load the rest. */
export interface PersonGrpListPage<TNode> {
  nodes: TNode[];
  pageInfo: {
    hasNextPage: boolean;
    endCursor?: string | null;
  };
}

export interface PersonGrpListConnection<TNode> extends PersonGrpListPage<TNode> {
  totalCount: number;
}

export interface PersonGrpBenefitsQueryVariables {
  pgaId: string;
  paidOutFrom: string;
  paidOutTo: string;
}

const PERSON_GRP_PROGRAM_CONSENT_FIELDS = gql`
  fragment PersonGrpProgramConsentFields on BackofficeCommissionProgramConsentConnection {
    nodes {
      id
      commissionCampaignId
      crmPersonId
      attributionModel
      ownership
      startsAt
      endsAt
      status
      person {
        id
        displayName
        pgaId
      }
      campaign {
        id
        editionYear
        status
        attributionModel
        partner {
          id
          name
        }
        program {
          id
          name
        }
      }
    }
    totalCount
    pageInfo {
      hasNextPage
      endCursor
    }
  }
`;

/** The focused-page FAA list: the shared FAA fields plus each FAA's notes, for the Notes column. */
const PERSON_GRP_PROGRAM_CONSENT_LIST_FIELDS = gql`
  fragment PersonGrpProgramConsentListFields on BackofficeCommissionProgramConsentConnection {
    ...PersonGrpProgramConsentFields
    nodes {
      id
      notes {
        ...CRMNoteFields
      }
    }
  }
  ${PERSON_GRP_PROGRAM_CONSENT_FIELDS}
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

const PERSON_GRP_LINE_FIELDS = gql`
  fragment PersonGrpLineFields on BackofficeCommissionLineConnection {
    nodes {
      id
      person {
        id
        displayName
        pgaId
      }
      amountCents
      fundingStatus
      payoutEligibilityStatus
      holdReasons
      lineKind
      receivedAt
      payableAt
      paidOutAt
      expiredAt
      crmFacilityId
      facilityName
      sourceRowReference
      ingestBatch {
        id
        filename
      }
      allocationAgreement {
        id
        status
        receiverCrmPersonId
        splits {
          id
          crmPersonId
          pgaId
          displayName
          splitPercent
        }
      }
      fundingBatch {
        id
        status
      }
      releaseBatch {
        id
        status
      }
      splitLines {
        id
      }
      campaign {
        id
        editionYear
        partner {
          id
          name
        }
        program {
          id
          name
        }
      }
    }
    totalCount
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
  }
`;

const PERSON_GRP_ALLOCATION_AGREEMENT_FIELDS = gql`
  fragment PersonGrpAllocationAgreementFields on BackofficeCommissionAllocationAgreementConnection {
    nodes {
      id
      commissionCampaignId
      receiverCrmPersonId
      crmFacilityId
      facilityName
      status
      effectiveStart
      effectiveEnd
      campaign {
        id
        editionYear
        status
        partner {
          id
          name
        }
      }
      splits {
        id
        crmPersonId
        pgaId
        displayName
        splitPercent
        hasActiveFaa
      }
    }
    totalCount
    pageInfo {
      hasNextPage
      endCursor
    }
  }
`;

/** The focused-page IAA lists: the shared IAA fields plus each IAA's notes, for the Notes column. */
const PERSON_GRP_ALLOCATION_AGREEMENT_LIST_FIELDS = gql`
  fragment PersonGrpAllocationAgreementListFields on BackofficeCommissionAllocationAgreementConnection {
    ...PersonGrpAllocationAgreementFields
    nodes {
      id
      notes {
        ...CRMNoteFields
      }
    }
  }
  ${PERSON_GRP_ALLOCATION_AGREEMENT_FIELDS}
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export const PERSON_GRP_BENEFITS_QUERY: TypedDocumentNode<
  PersonGrpBenefitsQueryResponse,
  PersonGrpBenefitsQueryVariables
> = gql`
  query PersonGrpBenefits($pgaId: ID!, $paidOutFrom: ISO8601Date!, $paidOutTo: ISO8601Date!) {
    person(pgaId: $pgaId) {
      id
      displayName
      pgaId
      commissionBenefitProgram(programSlug: "grp") {
        program {
          id
          name
          slug
        }
        enrollment {
          id
          accountStatus
          program {
            id
            name
            slug
          }
        }
        # 100 is pga-backend's default_max_page_size; larger values are clamped. usePersonGrpLists loads the rest.
        programConsents(first: 100) {
          ...PersonGrpProgramConsentListFields
        }
        allocationAgreements(first: 100) {
          ...PersonGrpAllocationAgreementListFields
        }
        receivedAllocationAgreements(first: 100) {
          ...PersonGrpAllocationAgreementListFields
        }
        lines(first: 100) {
          ...PersonGrpLineFields
        }
        paidOutTotal(from: $paidOutFrom, to: $paidOutTo) {
          amountCents
          lineCount
          from
          to
        }
      }
    }
  }
  ${PERSON_GRP_PROGRAM_CONSENT_LIST_FIELDS}
  ${PERSON_GRP_ALLOCATION_AGREEMENT_LIST_FIELDS}
  ${PERSON_GRP_LINE_FIELDS}
`;

interface PersonGrpListPageVariables {
  pgaId: string;
  after: string;
}

interface PersonGrpListPageResponse<TKey extends string, TNode> {
  person: {
    id: string;
    commissionBenefitProgram: Record<TKey, PersonGrpListConnection<TNode>> | null;
  };
}

export const PERSON_GRP_PROGRAM_CONSENTS_PAGE_QUERY: TypedDocumentNode<
  PersonGrpListPageResponse<"programConsents", CommissionProgramConsentNode>,
  PersonGrpListPageVariables
> = gql`
  query PersonGrpProgramConsentsPage($pgaId: ID!, $after: String!) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        programConsents(first: 100, after: $after) {
          ...PersonGrpProgramConsentListFields
        }
      }
    }
  }
  ${PERSON_GRP_PROGRAM_CONSENT_LIST_FIELDS}
`;

export const PERSON_GRP_ALLOCATION_AGREEMENTS_PAGE_QUERY: TypedDocumentNode<
  PersonGrpListPageResponse<"allocationAgreements", PersonGrpAllocationAgreementNode>,
  PersonGrpListPageVariables
> = gql`
  query PersonGrpAllocationAgreementsPage($pgaId: ID!, $after: String!) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        allocationAgreements(first: 100, after: $after) {
          ...PersonGrpAllocationAgreementListFields
        }
      }
    }
  }
  ${PERSON_GRP_ALLOCATION_AGREEMENT_LIST_FIELDS}
`;

export const PERSON_GRP_RECEIVED_ALLOCATION_AGREEMENTS_PAGE_QUERY: TypedDocumentNode<
  PersonGrpListPageResponse<"receivedAllocationAgreements", PersonGrpAllocationAgreementNode>,
  PersonGrpListPageVariables
> = gql`
  query PersonGrpReceivedAllocationAgreementsPage($pgaId: ID!, $after: String!) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        receivedAllocationAgreements(first: 100, after: $after) {
          ...PersonGrpAllocationAgreementListFields
        }
      }
    }
  }
  ${PERSON_GRP_ALLOCATION_AGREEMENT_LIST_FIELDS}
`;

export const PERSON_GRP_LINES_PAGE_QUERY: TypedDocumentNode<
  PersonGrpListPageResponse<"lines", PersonGrpContributionLineNode>,
  PersonGrpListPageVariables
> = gql`
  query PersonGrpLinesPage($pgaId: ID!, $after: String!) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        lines(first: 100, after: $after) {
          ...PersonGrpLineFields
        }
      }
    }
  }
  ${PERSON_GRP_LINE_FIELDS}
`;

interface PersonGrpFaaLinesVariables {
  pgaId: string;
  campaignId: string;
}

/** The member's lines on one campaign, for the FAA page's Contributions; usePersonGrpFaaContributions loads the rest. */
export const PERSON_GRP_FAA_LINES_QUERY: TypedDocumentNode<
  PersonGrpListPageResponse<"lines", PersonGrpContributionLineNode>,
  PersonGrpFaaLinesVariables
> = gql`
  query PersonGrpFaaLines($pgaId: ID!, $campaignId: ID!) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        lines(campaignId: $campaignId, first: 100) {
          ...PersonGrpLineFields
        }
      }
    }
  }
  ${PERSON_GRP_LINE_FIELDS}
`;

export const PERSON_GRP_FAA_LINES_PAGE_QUERY: TypedDocumentNode<
  PersonGrpListPageResponse<"lines", PersonGrpContributionLineNode>,
  PersonGrpFaaLinesVariables & { after: string }
> = gql`
  query PersonGrpFaaLinesPage($pgaId: ID!, $campaignId: ID!, $after: String!) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        lines(campaignId: $campaignId, first: 100, after: $after) {
          ...PersonGrpLineFields
        }
      }
    }
  }
  ${PERSON_GRP_LINE_FIELDS}
`;

/** Not Yet Checked (`ACCRUED`) lines for the person GRP tab when the tab query's first page of lines is not every line. */
export const PERSON_GRP_NOT_YET_CHECKED_LINES_QUERY: TypedDocumentNode<
  PersonGrpListPageResponse<"lines", PersonGrpContributionLineNode>,
  { pgaId: string; after?: string }
> = gql`
  query PersonGrpNotYetCheckedLines($pgaId: ID!, $after: String) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        lines(payoutEligibilityStatus: ACCRUED, first: 100, after: $after) {
          ...PersonGrpLineFields
        }
      }
    }
  }
  ${PERSON_GRP_LINE_FIELDS}
`;

/** Later pages of every line, with only what the sponsor charts, On Hold totals, and authorization issues read. */
export const PERSON_GRP_LINE_TOTALS_PAGE_QUERY: TypedDocumentNode<
  PersonGrpListPageResponse<"lines", PersonGrpContributionTotalsLine>,
  PersonGrpListPageVariables
> = gql`
  query PersonGrpLineTotalsPage($pgaId: ID!, $after: String!) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        lines(first: 100, after: $after) {
          nodes {
            id
            amountCents
            payoutEligibilityStatus
            holdReasons
            receivedAt
            campaign {
              id
              editionYear
              partner {
                id
                name
              }
            }
          }
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;

export interface PersonGrpEmploymentFacilityQueryResponse {
  person: { id: string; primaryFacility: { id: string; name: string } | null };
}

/** The facility on the member's active primary employment, for the Facility Authorization card (`viewPersonCareer`). */
export const PERSON_GRP_EMPLOYMENT_FACILITY_QUERY: TypedDocumentNode<
  PersonGrpEmploymentFacilityQueryResponse,
  { pgaId: string }
> = gql`
  query PersonGrpEmploymentFacility($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      primaryFacility {
        id
        name
      }
    }
  }
`;

export interface PersonGrpIaaSetupQueryResponse {
  person: PersonGrpContextQueryResponse["person"] & {
    primaryFacility: { id: string; name: string } | null;
    commissionBenefitProgram: {
      programConsents: PersonGrpListConnection<CommissionProgramConsentNode>;
      allocationAgreements: PersonGrpListConnection<PersonGrpAllocationAgreementNode>;
    } | null;
  };
}

/** The member's FAAs and own IAAs for the Add Authorization(s) wizard; usePersonGrpLists loads later pages. */
export const PERSON_GRP_IAA_SETUP_QUERY: TypedDocumentNode<PersonGrpIaaSetupQueryResponse, { pgaId: string }> = gql`
  query PersonGrpIaaSetup($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      displayName
      pgaId
      primaryFacility {
        id
        name
      }
      commissionBenefitProgram(programSlug: "grp") {
        programConsents(first: 100) {
          ...PersonGrpProgramConsentFields
        }
        allocationAgreements(first: 100) {
          ...PersonGrpAllocationAgreementFields
        }
      }
    }
  }
  ${PERSON_GRP_PROGRAM_CONSENT_FIELDS}
  ${PERSON_GRP_ALLOCATION_AGREEMENT_FIELDS}
`;

export interface PersonGrpSplitteeQueryResponse {
  person:
    | (PersonGrpContextQueryResponse["person"] & {
        commissionBenefitProgram: {
          programConsents: PersonGrpListPage<
            Pick<CommissionProgramConsentNode, "id" | "commissionCampaignId" | "status" | "startsAt" | "endsAt">
          >;
        } | null;
      })
    | null;
}

/** A splittee looked up by PGA ID, with a page of their Active FAAs; later pages follow `endCursor`. */
export const PERSON_GRP_SPLITTEE_QUERY: TypedDocumentNode<
  PersonGrpSplitteeQueryResponse,
  { pgaId: string; after?: string }
> = gql`
  query PersonGrpSplittee($pgaId: ID!, $after: String) {
    person(pgaId: $pgaId) {
      id
      displayName
      pgaId
      commissionBenefitProgram(programSlug: "grp") {
        programConsents(status: ACTIVE, first: 100, after: $after) {
          nodes {
            id
            commissionCampaignId
            status
            startsAt
            endsAt
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
`;

export interface PersonGrpAllocationAgreementQueryResponse {
  person: PersonGrpContextQueryResponse["person"];
  commissionAllocationAgreementById: PersonGrpAllocationAgreementNode | null;
}

export interface PersonGrpFaaRelatedIaasQueryResponse {
  person: {
    id: string;
    commissionBenefitProgram: {
      program: { id: string };
      allocationAgreements: PersonGrpListConnection<PersonGrpAllocationAgreementNode>;
      receivedAllocationAgreements: PersonGrpListConnection<PersonGrpAllocationAgreementNode>;
    } | null;
  };
}

/** First pages of the member's own and received IAAs for the FAA page's Related IAAs; usePersonGrpLists loads the rest. */
export const PERSON_GRP_FAA_RELATED_IAAS_QUERY: TypedDocumentNode<
  PersonGrpFaaRelatedIaasQueryResponse,
  { pgaId: string }
> = gql`
  query PersonGrpFaaRelatedIaas($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        program {
          id
        }
        allocationAgreements(first: 100) {
          ...PersonGrpAllocationAgreementFields
        }
        receivedAllocationAgreements(first: 100) {
          ...PersonGrpAllocationAgreementFields
        }
      }
    }
  }
  ${PERSON_GRP_ALLOCATION_AGREEMENT_FIELDS}
`;

/** One IAA by id, plus the page's member so the page can confirm that member shares it. */
export const PERSON_GRP_ALLOCATION_AGREEMENT_QUERY: TypedDocumentNode<
  PersonGrpAllocationAgreementQueryResponse,
  { pgaId: string; iaaId: string }
> = gql`
  query PersonGrpAllocationAgreement($pgaId: ID!, $iaaId: ID!) {
    person(pgaId: $pgaId) {
      id
      displayName
      pgaId
    }
    commissionAllocationAgreementById(id: $iaaId) {
      id
      commissionCampaignId
      receiverCrmPersonId
      crmFacilityId
      facilityName
      status
      effectiveStart
      effectiveEnd
      campaign {
        id
        editionYear
        status
        partner {
          id
          name
        }
      }
      splits {
        id
        crmPersonId
        pgaId
        displayName
        splitPercent
        hasActiveFaa
      }
    }
  }
`;

export interface PersonGrpEnrollmentQueryResponse {
  grpEnrollments: {
    nodes: PersonGrpEnrollmentNode[];
    pageInfo: {
      hasNextPage: boolean;
      endCursor?: string | null;
    };
  };
}

export interface PersonGrpEnrollmentQueryVariables {
  first?: number;
  after?: string;
  filters?: {
    search?: string;
    status?: GrpEnrollmentStatus;
  };
}

export const PERSON_GRP_ENROLLMENT_QUERY: TypedDocumentNode<
  PersonGrpEnrollmentQueryResponse,
  PersonGrpEnrollmentQueryVariables
> = gql`
  query PersonGrpEnrollment($first: Int, $after: String, $filters: BackofficeGrpEnrollmentsInput) {
    grpEnrollments(first: $first, after: $after, filters: $filters) {
      nodes {
        id
        accountOpenedOn
        person {
          pgaId
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
